import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { pretty } from "@/lib/helpers";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShieldCheck, LogOut, Plus, ExternalLink, Trash2, Building2, Users, Activity, CheckCircle2, XCircle, AlertTriangle, Loader2, Bot, Wallet, Plug } from "lucide-react";
import { toast } from "sonner";

const emptyRest = { name: "", address: "", postcode: "", public_phone_number: "", twilio_routing_number: "", handover_phone_number: "", ai_enabled: true, subscription_plan: "STARTER" };
const ROLES = ["RESTAURANT_OWNER", "RESTAURANT_MANAGER", "STAFF"];

function RestaurantDialog({ restaurant, onSaved, trigger }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(emptyRest);
  useEffect(() => { if (open) setF(restaurant ? { ...emptyRest, ...restaurant } : emptyRest); }, [open, restaurant]);
  const up = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.name) return toast.error("Enter a name");
    try {
      if (restaurant) await api.put(`/restaurants/${restaurant.id}`, f);
      else await api.post(`/restaurants`, f);
      toast.success("Restaurant saved"); setOpen(false); onSaved();
    } catch (e) { toast.error("Could not save"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle className="font-display">{restaurant ? "Edit" : "Create"} restaurant</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2"><Label>Name</Label><Input className="h-11 mt-1.5" value={f.name} onChange={(e) => up("name", e.target.value)} data-testid="rest-name" /></div>
          <div><Label>Address</Label><Input className="h-11 mt-1.5" value={f.address || ""} onChange={(e) => up("address", e.target.value)} /></div>
          <div><Label>Postcode</Label><Input className="h-11 mt-1.5" value={f.postcode || ""} onChange={(e) => up("postcode", e.target.value)} /></div>
          <div><Label>Public phone number</Label><Input className="h-11 mt-1.5" value={f.public_phone_number || ""} onChange={(e) => up("public_phone_number", e.target.value)} /></div>
          <div><Label>Hidden Twilio routing number</Label><Input className="h-11 mt-1.5" value={f.twilio_routing_number || ""} onChange={(e) => up("twilio_routing_number", e.target.value)} data-testid="rest-routing-number" /></div>
          <div><Label>Handover number</Label><Input className="h-11 mt-1.5" value={f.handover_phone_number || ""} onChange={(e) => up("handover_phone_number", e.target.value)} /></div>
          <div><Label>Subscription plan</Label>
            <Select value={f.subscription_plan} onValueChange={(v) => up("subscription_plan", v)}>
              <SelectTrigger className="h-11 mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>{["STARTER", "PRO", "ENTERPRISE"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select></div>
        </div>
        <DialogFooter><Button className="h-11" onClick={save} data-testid="save-restaurant-admin">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UserDialog({ restaurants, onSaved }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ email: "", password: "", name: "", role: "STAFF", restaurant_id: "", phone: "" });
  const up = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.email || !f.password || !f.name) return toast.error("Fill required fields");
    try { await api.post("/users", { ...f, restaurant_id: f.restaurant_id || null }); toast.success("User created"); setOpen(false); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not create user"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="h-11 gap-2" data-testid="new-user-button"><Plus className="h-4 w-4" /> New user</Button></DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-display">Create user</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Name</Label><Input className="h-11 mt-1.5" value={f.name} onChange={(e) => up("name", e.target.value)} data-testid="user-name" /></div>
          <div><Label>Email</Label><Input className="h-11 mt-1.5" value={f.email} onChange={(e) => up("email", e.target.value)} data-testid="user-email" /></div>
          <div><Label>Password</Label><Input type="password" className="h-11 mt-1.5" value={f.password} onChange={(e) => up("password", e.target.value)} data-testid="user-password" /></div>
          <div><Label>Phone</Label><Input className="h-11 mt-1.5" value={f.phone} onChange={(e) => up("phone", e.target.value)} /></div>
          <div><Label>Role</Label>
            <Select value={f.role} onValueChange={(v) => up("role", v)}>
              <SelectTrigger className="h-11 mt-1.5" data-testid="user-role"><SelectValue /></SelectTrigger>
              <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{pretty(r)}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label>Restaurant</Label>
            <Select value={f.restaurant_id} onValueChange={(v) => up("restaurant_id", v)}>
              <SelectTrigger className="h-11 mt-1.5" data-testid="user-restaurant"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{restaurants.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
            </Select></div>
        </div>
        <DialogFooter><Button className="h-11" onClick={save} data-testid="save-user">Create user</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminConsole() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [restaurants, setRestaurants] = useState([]);
  const [users, setUsers] = useState([]);
  const [aiRules, setAiRules] = useState({ base_rules: "", locked_rules: "" });
  const [billingConfig, setBillingConfig] = useState(null);
  const [billingSummary, setBillingSummary] = useState(null);
  const [topups, setTopups] = useState({});
  const [subPlans, setSubPlans] = useState([]);
  const [featureCatalog, setFeatureCatalog] = useState([]);
  const [testOpen, setTestOpen] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResults, setTestResults] = useState(null);
  const [hubrise, setHubrise] = useState({ configured: false, client_id: "", has_secret: false });
  const [hubriseSecret, setHubriseSecret] = useState("");

  const runTest = async () => {
    setTesting(true);
    setTestOpen(true);
    setTestResults(null);
    try {
      const { data } = await api.get("/admin/test-integrations");
      setTestResults(data);
    } catch (e) {
      toast.error("Could not run integration test");
      setTestResults({ error: true });
    } finally {
      setTesting(false);
    }
  };

  const load = useCallback(() => {
    api.get("/restaurants").then((r) => setRestaurants(r.data));
    api.get("/users").then((r) => setUsers(r.data));
    api.get("/admin/ai-base-rules").then((r) => setAiRules({ base_rules: r.data.base_rules || "", locked_rules: r.data.locked_rules || "" })).catch(() => {});
    api.get("/admin/billing-config").then((r) => setBillingConfig(r.data)).catch(() => {});
    api.get("/admin/billing-summary").then((r) => setBillingSummary(r.data)).catch(() => {});
    api.get("/admin/plans").then((r) => setSubPlans(r.data)).catch(() => {});
    api.get("/features/catalog").then((r) => setFeatureCatalog(r.data.features)).catch(() => {});
    api.get("/admin/hubrise-config").then((r) => setHubrise(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveHubrise = async () => {
    if (!hubrise.client_id) return toast.error("Enter the HubRise Client ID");
    const body = { client_id: hubrise.client_id };
    if (hubriseSecret) body.client_secret = hubriseSecret;
    const { data } = await api.put("/admin/hubrise-config", body);
    setHubrise(data);
    setHubriseSecret("");
    toast.success("HubRise credentials saved");
  };

  const setPlanField = (idx, patch) => setSubPlans((p) => p.map((x, i) => i === idx ? { ...x, ...patch } : x));
  const setPlanFeature = (idx, key, val) => setSubPlans((p) => p.map((x, i) => i === idx ? { ...x, features: { ...x.features, [key]: val } } : x));
  const savePlan = async (plan) => { await api.put(`/admin/plans/${plan.id}`, plan); toast.success(`${plan.name} saved`); load(); };
  const createPlan = async () => { await api.post("/admin/plans", { name: "New plan", price_monthly: 0, features: {} }); toast.success("Plan created"); load(); };
  const assignPlan = async (rid, plan_id) => { await api.put(`/admin/restaurants/${rid}/plan`, { plan_id }); toast.success("Plan assigned"); load(); };

  const saveAiRules = async () => {
    await api.put("/admin/ai-base-rules", aiRules);
    toast.success("AI base rules saved — applies to all restaurants");
  };

  const saveBillingConfig = async () => {
    await api.put("/admin/billing-config", billingConfig);
    toast.success("Billing rates saved");
    load();
  };

  const doTopup = async (rid) => {
    const amount = parseFloat(topups[rid]);
    if (!amount || amount <= 0) return toast.error("Enter a top-up amount");
    await api.post(`/restaurants/${rid}/credit/topup`, { amount });
    toast.success(`Topped up ${amount}`);
    setTopups((p) => ({ ...p, [rid]: "" }));
    load();
  };

  const toggleAi = async (r, v) => { await api.patch(`/restaurants/${r.id}/ai`, { ai_enabled: v }); load(); toast.success(`AI ${v ? "enabled" : "disabled"} for ${r.name}`); };
  const delUser = async (id) => { await api.delete(`/users/${id}`); load(); };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-20 border-b border-border bg-card">
        <div className="max-w-[1300px] mx-auto flex items-center justify-between px-6 h-16">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center"><ShieldCheck className="h-5 w-5 text-primary-foreground" /></div>
            <div><p className="font-display font-bold leading-tight">Platform Admin</p><p className="text-xs text-muted-foreground">{user?.name}</p></div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" className="gap-2 h-10" onClick={runTest} data-testid="test-connection-button">
              <Activity className="h-4 w-4" /> Test connection
            </Button>
            <Button variant="outline" className="gap-2 h-10" onClick={() => { logout(); navigate("/login"); }} data-testid="admin-logout"><LogOut className="h-4 w-4" /> Log out</Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1300px] mx-auto p-6 fade-up">
        <h1 className="font-display text-3xl font-bold mb-6">Platform console</h1>
        <Tabs defaultValue="restaurants">
          <TabsList>
            <TabsTrigger value="restaurants" data-testid="tab-restaurants"><Building2 className="h-4 w-4 mr-1.5" /> Restaurants</TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users"><Users className="h-4 w-4 mr-1.5" /> Users</TabsTrigger>
            <TabsTrigger value="ai-rules" data-testid="tab-ai-rules"><Bot className="h-4 w-4 mr-1.5" /> AI Rules</TabsTrigger>
            <TabsTrigger value="billing" data-testid="tab-billing"><Wallet className="h-4 w-4 mr-1.5" /> Billing</TabsTrigger>
            <TabsTrigger value="plans" data-testid="tab-plans"><Wallet className="h-4 w-4 mr-1.5" /> Plans</TabsTrigger>
            <TabsTrigger value="integrations" data-testid="tab-integrations"><Plug className="h-4 w-4 mr-1.5" /> Integrations</TabsTrigger>
          </TabsList>

          <TabsContent value="restaurants" className="mt-6">
            <div className="flex justify-end mb-4">
              <RestaurantDialog onSaved={load} trigger={<Button className="h-11 gap-2" data-testid="new-restaurant-button"><Plus className="h-4 w-4" /> New restaurant</Button>} />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {restaurants.map((r) => (
                <Card key={r.id} data-testid={`restaurant-card-${r.id}`} className="p-6 rounded-2xl">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-display font-bold text-lg">{r.name}</p>
                      <p className="text-sm text-muted-foreground">{r.address} · {r.postcode}</p>
                    </div>
                    <Badge variant="outline">{r.subscription_plan}</Badge>
                  </div>
                  <div className="mt-4 space-y-1 text-sm">
                    <p className="text-muted-foreground">Public: <span className="text-foreground">{r.public_phone_number || "—"}</span></p>
                    <p className="text-muted-foreground">Routing (hidden): <span className="text-foreground">{r.twilio_routing_number || "not assigned"}</span></p>
                    <p className="text-muted-foreground">Handover: <span className="text-foreground">{r.handover_phone_number || "—"}</span></p>
                  </div>
                  <div className="mt-4 flex items-center justify-between pt-4 border-t border-border">
                    <div className="flex items-center gap-2">
                      <Switch checked={r.ai_enabled} onCheckedChange={(v) => toggleAi(r, v)} data-testid={`ai-toggle-${r.id}`} />
                      <span className="text-sm">AI {r.ai_enabled ? "on" : "off"}</span>
                    </div>
                    <div className="flex gap-2">
                      <RestaurantDialog restaurant={r} onSaved={load} trigger={<Button variant="outline" size="sm" data-testid={`edit-restaurant-${r.id}`}>Edit</Button>} />
                      <Button size="sm" className="gap-1" onClick={() => navigate(`/r/${r.id}/dashboard`)} data-testid={`open-restaurant-${r.id}`}><ExternalLink className="h-3.5 w-3.5" /> Open</Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="users" className="mt-6">
            <div className="flex justify-end mb-4"><UserDialog restaurants={restaurants} onSaved={load} /></div>
            <Card className="rounded-2xl divide-y">
              {users.map((u) => {
                const rest = restaurants.find((r) => r.id === u.restaurant_id);
                return (
                  <div key={u.id} data-testid={`user-row-${u.id}`} className="flex items-center justify-between p-4">
                    <div>
                      <p className="font-medium">{u.name} <span className="text-muted-foreground text-sm">· {u.email}</span></p>
                      <p className="text-sm text-muted-foreground">{pretty(u.role)}{rest ? ` · ${rest.name}` : ""}</p>
                    </div>
                    {u.role !== "PLATFORM_ADMIN" && (
                      <Button size="icon" variant="ghost" onClick={() => delUser(u.id)} data-testid={`del-user-${u.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                    )}
                  </div>
                );
              })}
            </Card>
          </TabsContent>

          <TabsContent value="ai-rules" className="mt-6">
            <Card className="p-6 rounded-2xl max-w-3xl space-y-5">
              <div>
                <p className="font-display font-semibold">Global base AI rules</p>
                <p className="text-sm text-muted-foreground">Applied to every restaurant's AI receptionist, on top of the built-in safety & allergen rules. Restaurants can add their own instructions in their own settings, but those never override safety.</p>
                <Textarea className="mt-2 min-h-32" data-testid="ai-base-rules"
                  placeholder="e.g. Always thank the caller by name. Mention the current promotion once. Keep calls under 4 minutes."
                  value={aiRules.base_rules} onChange={(e) => setAiRules({ ...aiRules, base_rules: e.target.value })} />
              </div>
              <div>
                <p className="font-display font-semibold">Locked (non-overridable) rules</p>
                <p className="text-sm text-muted-foreground">Highest-priority rules that no restaurant-specific instruction can override.</p>
                <Textarea className="mt-2 min-h-32" data-testid="ai-locked-rules"
                  placeholder="e.g. Never take card payments over the phone. Never give allergy safety guarantees. Always transfer complaints to staff."
                  value={aiRules.locked_rules} onChange={(e) => setAiRules({ ...aiRules, locked_rules: e.target.value })} />
              </div>
              <Button className="h-11" onClick={saveAiRules} data-testid="save-ai-rules">Save AI rules</Button>
            </Card>
          </TabsContent>

          <TabsContent value="billing" className="mt-6 space-y-6">
            <Card className="p-6 rounded-2xl">
              <p className="font-display font-semibold mb-1">Cost & customer rates</p>
              <p className="text-sm text-muted-foreground mb-4">Actual platform cost vs the customer rate restaurants are charged (per minute of call / per SMS). Edit and save.</p>
              {billingConfig && (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[
                    ["cost_twilio_voice_per_min", "Cost: Twilio voice / min"],
                    ["cost_openai_per_min", "Cost: OpenAI / min"],
                    ["cost_twilio_sms", "Cost: Twilio SMS each"],
                    ["charge_voice_per_min", "Customer rate: voice / min"],
                    ["charge_sms", "Customer rate: SMS each"],
                  ].map(([k, lbl]) => (
                    <div key={k}>
                      <Label className="text-xs">{lbl} ({billingConfig.currency})</Label>
                      <Input type="number" step="0.001" className="h-11 mt-1.5" data-testid={`billing-${k}`}
                        value={billingConfig[k] ?? 0} onChange={(e) => setBillingConfig({ ...billingConfig, [k]: parseFloat(e.target.value) })} />
                    </div>
                  ))}
                </div>
              )}
              <Button className="h-11 mt-4" onClick={saveBillingConfig} data-testid="save-billing-config">Save rates</Button>
            </Card>

            {billingSummary && (
              <Card className="p-6 rounded-2xl">
                <div className="flex flex-wrap gap-4 mb-4 text-sm">
                  <span className="text-muted-foreground">Total actual cost: <b className="text-foreground">£{billingSummary.totals.actual_cost}</b></span>
                  <span className="text-muted-foreground">Total customer charge: <b className="text-foreground">£{billingSummary.totals.customer_charge}</b></span>
                  <span className="text-muted-foreground">Total margin: <b className="text-emerald-600">£{billingSummary.totals.margin}</b></span>
                </div>
                <div className="space-y-2">
                  {billingSummary.rows.map((row) => (
                    <div key={row.restaurant_id} data-testid={`billing-row-${row.restaurant_id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border p-4">
                      <div className="min-w-40">
                        <p className="font-medium">{row.name}</p>
                        <p className="text-sm text-muted-foreground">{row.calls_count} calls · {row.sms_count} SMS</p>
                      </div>
                      <div className="text-sm text-right">
                        <p>Balance: <b className={row.credit_balance < 5 ? "text-red-600" : "text-foreground"}>£{row.credit_balance}</b></p>
                        <p className="text-muted-foreground">Charged £{row.customer_charge} · cost £{row.actual_cost} · margin £{row.margin}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Input type="number" step="1" placeholder="£" className="h-10 w-24" data-testid={`topup-input-${row.restaurant_id}`}
                          value={topups[row.restaurant_id] || ""} onChange={(e) => setTopups((p) => ({ ...p, [row.restaurant_id]: e.target.value }))} />
                        <Button className="h-10" data-testid={`topup-button-${row.restaurant_id}`} onClick={() => doTopup(row.restaurant_id)}>Top up</Button>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="plans" className="mt-6 space-y-6">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">Define subscription plans and toggle which features each unlocks, then assign a plan per restaurant.</p>
              <Button variant="outline" className="h-10 gap-1" onClick={createPlan} data-testid="create-plan"><Plus className="h-4 w-4" /> New plan</Button>
            </div>
            <div className="grid lg:grid-cols-3 gap-4">
              {subPlans.map((plan, idx) => (
                <Card key={plan.id} data-testid={`plan-card-${plan.id}`} className="p-5 rounded-2xl space-y-3">
                  <Input className="h-10 font-display font-bold" value={plan.name} data-testid={`plan-name-${plan.id}`}
                    onChange={(e) => setPlanField(idx, { name: e.target.value })} />
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">£</span>
                    <Input type="number" step="1" className="h-10" value={plan.price_monthly}
                      onChange={(e) => setPlanField(idx, { price_monthly: parseFloat(e.target.value) })} data-testid={`plan-price-${plan.id}`} />
                    <span className="text-sm text-muted-foreground">/mo</span>
                  </div>
                  <div className="space-y-1.5 pt-1">
                    {featureCatalog.map((f) => (
                      <div key={f.key} className="flex items-center justify-between">
                        <span className="text-sm">{f.label}</span>
                        <Switch checked={!!(plan.features || {})[f.key]} data-testid={`plan-${plan.id}-feat-${f.key}`}
                          onCheckedChange={(v) => setPlanFeature(idx, f.key, v)} />
                      </div>
                    ))}
                  </div>
                  <Button className="h-10 w-full" onClick={() => savePlan(plan)} data-testid={`save-plan-${plan.id}`}>Save plan</Button>
                </Card>
              ))}
            </div>

            <Card className="p-5 rounded-2xl">
              <p className="font-display font-semibold mb-3">Assign plans to restaurants</p>
              <div className="space-y-2">
                {restaurants.map((r) => (
                  <div key={r.id} data-testid={`assign-plan-row-${r.id}`} className="flex items-center justify-between rounded-lg border border-border p-3">
                    <span className="font-medium">{r.name}</span>
                    <select className="h-10 rounded-lg border border-border bg-background px-3 text-sm" data-testid={`assign-plan-${r.id}`}
                      value={r.plan_id || ""} onChange={(e) => assignPlan(r.id, e.target.value)}>
                      <option value="" disabled>Select plan…</option>
                      {subPlans.map((p) => <option key={p.id} value={p.id}>{p.name} (£{p.price_monthly}/mo)</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="integrations" className="mt-6">
            <Card className="p-6 rounded-2xl max-w-2xl" data-testid="hubrise-admin-card">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2.5">
                  <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center"><Plug className="h-5 w-5 text-primary" /></div>
                  <div>
                    <p className="font-display font-semibold text-lg">HubRise</p>
                    <p className="text-sm text-muted-foreground">Platform OAuth client — powers every restaurant's HubRise connection</p>
                  </div>
                </div>
                <Badge className={hubrise.configured ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-stone-100 text-stone-600"} data-testid="hubrise-admin-status">
                  {hubrise.configured ? <><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Active</> : "Not configured"}
                </Badge>
              </div>
              <div className="rounded-lg bg-secondary/60 p-3 my-4 text-sm text-muted-foreground">
                Create an OAuth 2.0 client in the <a href="https://manager.hubrise.com" target="_blank" rel="noreferrer" className="text-primary hover:underline inline-flex items-center gap-0.5">HubRise back office <ExternalLink className="h-3 w-3" /></a> → <b>Settings → Developer → Create an OAuth 2.0 client</b>, then paste the credentials here. All restaurants connect through this single client.
              </div>
              <div className="space-y-4">
                <div>
                  <Label>Client ID</Label>
                  <Input className="h-11 mt-1.5" value={hubrise.client_id || ""} data-testid="hubrise-client-id"
                    onChange={(e) => setHubrise({ ...hubrise, client_id: e.target.value })} placeholder="xxxxx.clients.hubrise.com" />
                </div>
                <div>
                  <Label>Client Secret {hubrise.has_secret && <span className="text-xs text-emerald-600">(saved — leave blank to keep)</span>}</Label>
                  <Input type="password" className="h-11 mt-1.5" value={hubriseSecret} data-testid="hubrise-client-secret"
                    onChange={(e) => setHubriseSecret(e.target.value)} placeholder={hubrise.has_secret ? "••••••••••••" : "Paste client secret"} />
                </div>
                <Button className="h-11" onClick={saveHubrise} data-testid="hubrise-save-config">Save HubRise credentials</Button>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      <Dialog open={testOpen} onOpenChange={setTestOpen}>
        <DialogContent className="max-w-lg" data-testid="test-results-dialog">
          <DialogHeader><DialogTitle className="font-display">Integration health check</DialogTitle>
            <DialogDescription>Live status of your OpenAI Realtime and Twilio connections.</DialogDescription>
          </DialogHeader>
          {testing || !testResults ? (
            <div className="flex items-center gap-3 py-8 justify-center text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" /> Testing OpenAI Realtime & Twilio…
            </div>
          ) : testResults.error ? (
            <p className="text-red-600">Could not run the test. Please try again.</p>
          ) : (
            <div className="space-y-3">
              {[testResults.openai, testResults.twilio].map((r, i) => (
                <div key={i} data-testid={`test-result-${i === 0 ? "openai" : "twilio"}`}
                  className={`rounded-xl border p-4 ${r.ok ? (r.warning ? "border-amber-200 bg-amber-50" : "border-emerald-200 bg-emerald-50") : "border-red-200 bg-red-50"}`}>
                  <div className="flex items-center gap-2">
                    {r.ok ? (r.warning ? <AlertTriangle className="h-5 w-5 text-amber-600" /> : <CheckCircle2 className="h-5 w-5 text-emerald-600" />) : <XCircle className="h-5 w-5 text-red-600" />}
                    <p className="font-display font-semibold">{r.service}</p>
                    <span className={`ml-auto text-xs font-medium uppercase tracking-wide ${r.ok ? (r.warning ? "text-amber-700" : "text-emerald-700") : "text-red-700"}`}>
                      {r.ok ? (r.warning ? "Warning" : "Connected") : "Failed"}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">{r.detail}</p>
                </div>
              ))}
              <Button variant="outline" className="w-full h-11" onClick={runTest} data-testid="rerun-test">Run again</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
